<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import ContentCard from 'dashboard/components-next/teletel/ContentCard.vue';
import { visibleTeletelPermissionGroups } from 'dashboard/helper/teletelCallCenter';

/**
 * Teletel roles management: create/edit/delete custom roles built from the
 * Teletel permission keys, and assign them to agents. Administrators only -
 * the server enforces the same rule on every endpoint.
 */
const { t } = useI18n();
const store = useStore();

// Grouped permission keys (batches 0 + 1 + 2). Labels live in i18n.
const GROUPS = [
  {
    key: 'CONVERSATIONS',
    keys: [
      'conversation_manage',
      'conversation_unassigned_manage',
      'conversation_participating_manage',
      'conversation_assigned_scope_only',
    ],
  },
  {
    key: 'CRM',
    keys: [
      'contact_manage',
      'company_manage',
      'lifecycle_manage',
      'report_manage',
      'knowledge_base_manage',
    ],
  },
  { key: 'FLOWS', keys: ['flow_trigger', 'flow_manage', 'flow_template_use'] },
  {
    key: 'GOVERNANCE',
    keys: [
      'sla_manage',
      'audit_view',
      'supervisor_manage',
      'assistant_manage',
      'team_presence_view',
    ],
  },
  { key: 'CALL_CENTER', keys: ['call_center_manage'] },
  {
    key: 'CORE',
    keys: [
      'campaign_manage',
      'inbox_manage',
      'automation_manage',
      'label_manage',
      'team_manage',
      'agent_manage',
      'advanced_assignment_manage',
      'custom_attribute_manage',
      'integration_manage',
      'macro_manage',
    ],
  },
];

const roles = computed(() => store.getters['teletelRoles/getRoles']);
const uiFlags = computed(() => store.getters['teletelRoles/getUIFlags']);
const agents = computed(() => store.getters['agents/getAgents']);
const visibleGroups = computed(() => visibleTeletelPermissionGroups(GROUPS));
// One permission explanation open at a time; the text comes from the code's
// actual enforcement (server guards and policy extensions), not from guesses.
const DETAIL_MARK = '?';
const openDetail = ref('');
const toggleDetail = key => {
  openDetail.value = openDetail.value === key ? '' : key;
};

const editorOpen = ref(false);
const editorCard = ref(null);
const editing = reactive({
  id: null,
  name: '',
  description: '',
  permissions: [],
});
const assignment = reactive({ userId: '', roleId: '' });

// Which role rows are expanded to list their agents. The agents store already
// carries each member's custom_role_id, so no extra request is needed.
const expandedRoles = ref([]);
const toggleAssigned = roleId => {
  const index = expandedRoles.value.indexOf(roleId);
  if (index === -1) expandedRoles.value.push(roleId);
  else expandedRoles.value.splice(index, 1);
};
const isExpanded = roleId => expandedRoles.value.includes(roleId);
const agentsForRole = roleId =>
  agents.value.filter(agent => agent.custom_role_id === roleId);
const agentsWithoutRole = computed(() =>
  agents.value.filter(agent => !agent.custom_role_id)
);
const roleName = roleId =>
  roles.value.find(role => role.id === roleId)?.name || '';
const baseRoleLabel = agent =>
  t(`AGENT_MGMT.AGENT_TYPES.${(agent.role || 'agent').toUpperCase()}`);
// First three permission labels shown inline; the full list sits in the title.
const PERMISSION_PREVIEW = 3;
const permissionLabels = role =>
  role.permissions.map(key => t(`TELETEL_ROLES.PERMISSIONS.${key}`));
const permissionPreview = role =>
  permissionLabels(role).slice(0, PERMISSION_PREVIEW).join(', ');
const permissionRest = role =>
  Math.max(role.permissions.length - PERMISSION_PREVIEW, 0);
// Picking an agent pre-fills their current role so the admin sees what the
// assignment will replace before saving.
const onPickAgent = () => {
  const agent = agents.value.find(item => item.id === assignment.userId);
  assignment.roleId = agent?.custom_role_id || '';
};

onMounted(() => {
  store.dispatch('teletelRoles/get');
  store.dispatch('agents/get');
});

const openEditor = async (role = null) => {
  editing.id = role?.id || null;
  editing.name = role?.name || '';
  editing.description = role?.description || '';
  editing.permissions = [...(role?.permissions || [])];
  editorOpen.value = true;
  await nextTick();
  editorCard.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const togglePermission = key => {
  const index = editing.permissions.indexOf(key);
  if (index === -1) editing.permissions.push(key);
  else editing.permissions.splice(index, 1);
};

const saveRole = async () => {
  try {
    const payload = {
      name: editing.name,
      description: editing.description,
      permissions: editing.permissions,
    };
    if (editing.id) {
      await store.dispatch('teletelRoles/update', {
        id: editing.id,
        ...payload,
      });
    } else {
      await store.dispatch('teletelRoles/create', payload);
    }
    editorOpen.value = false;
    useAlert(t('TELETEL_ROLES.ACTIONS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TELETEL_ROLES.ACTIONS.ERROR')
    );
  }
};

const deleteRole = async role => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(t('TELETEL_ROLES.LIST.DELETE_CONFIRM', { name: role.name }))
  ) {
    return;
  }
  try {
    await store.dispatch('teletelRoles/delete', role.id);
    useAlert(t('TELETEL_ROLES.ACTIONS.DELETED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TELETEL_ROLES.ACTIONS.ERROR')
    );
  }
};

const applyAssignment = async (userId, customRoleId) => {
  try {
    await store.dispatch('teletelRoles/assign', { userId, customRoleId });
    // Refresh both lists so the counts and the expanded names agree.
    await Promise.all([
      store.dispatch('agents/get'),
      store.dispatch('teletelRoles/get'),
    ]);
    useAlert(t('TELETEL_ROLES.ASSIGN.DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('TELETEL_ROLES.ACTIONS.ERROR')
    );
  }
};

const assignRole = async () => {
  if (!assignment.userId) return;
  await applyAssignment(assignment.userId, assignment.roleId || null);
};

const removeRole = async agent => {
  await applyAssignment(agent.id, null);
};
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_ROLES.HEADER')"
      :description="t('TELETEL_ROLES.DESCRIPTION')"
    />

    <ContentCard :title="t('TELETEL_ROLES.LIST.TITLE')">
      <div class="flex justify-end mb-2">
        <NextButton sm @click="openEditor()">
          {{ t('TELETEL_ROLES.LIST.NEW') }}
        </NextButton>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-start text-n-slate-11">
            <th class="py-2 text-start">{{ t('TELETEL_ROLES.LIST.NAME') }}</th>
            <th class="text-start">
              {{ t('TELETEL_ROLES.LIST.PERMISSIONS') }}
            </th>
            <th class="text-start">{{ t('TELETEL_ROLES.LIST.ASSIGNED') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <template v-for="role in roles" :key="role.id">
            <tr class="border-t border-n-weak">
              <td class="py-2">
                <p class="font-medium text-n-slate-12">{{ role.name }}</p>
                <p class="text-xs text-n-slate-10">{{ role.description }}</p>
              </td>
              <td :title="permissionLabels(role).join(', ')">
                <span class="font-medium">{{ role.permissions.length }}</span>
                <span
                  v-if="role.permissions.length"
                  class="text-xs text-n-slate-11"
                >
                  - {{ permissionPreview(role) }}
                  <template v-if="permissionRest(role)">
                    {{
                      t('TELETEL_ROLES.LIST.MORE', {
                        count: permissionRest(role),
                      })
                    }}
                  </template>
                </span>
              </td>
              <td>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 text-n-brand disabled:text-n-slate-10 disabled:cursor-default"
                  :disabled="!role.assigned_users_count"
                  :aria-expanded="isExpanded(role.id)"
                  @click="toggleAssigned(role.id)"
                >
                  {{
                    t('TELETEL_ROLES.LIST.ASSIGNED_COUNT', {
                      count: role.assigned_users_count,
                    })
                  }}
                  <span v-if="role.assigned_users_count" class="text-xs">
                    {{ isExpanded(role.id) ? '\u25B4' : '\u25BE' }}
                  </span>
                </button>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="text-n-brand me-2"
                  @click="openEditor(role)"
                >
                  {{ t('TELETEL_ROLES.LIST.EDIT') }}
                </button>
                <button
                  type="button"
                  class="text-n-ruby-11"
                  @click="deleteRole(role)"
                >
                  {{ t('TELETEL_ROLES.LIST.DELETE') }}
                </button>
              </td>
            </tr>
            <tr v-if="isExpanded(role.id)" class="bg-n-solid-1">
              <td colspan="4" class="px-3 py-2">
                <ul class="flex flex-wrap gap-2">
                  <li
                    v-for="agent in agentsForRole(role.id)"
                    :key="agent.id"
                    class="flex items-center gap-2 px-2 py-1 border rounded-full border-n-weak bg-n-background"
                  >
                    <img
                      v-if="agent.thumbnail"
                      :src="agent.thumbnail"
                      :alt="agent.name"
                      class="rounded-full size-5"
                    />
                    <span class="text-n-slate-12">{{ agent.name }}</span>
                    <button
                      type="button"
                      class="text-xs text-n-ruby-11"
                      @click="removeRole(agent)"
                    >
                      {{ t('TELETEL_ROLES.LIST.REMOVE_ROLE') }}
                    </button>
                  </li>
                  <li
                    v-if="!agentsForRole(role.id).length"
                    class="text-xs text-n-slate-10"
                  >
                    {{ t('TELETEL_ROLES.LIST.ASSIGNED_LOADING') }}
                  </li>
                </ul>
              </td>
            </tr>
          </template>
          <tr v-if="!roles.length && !uiFlags.isFetching">
            <td colspan="4" class="py-4 text-center text-n-slate-10">
              {{ t('TELETEL_ROLES.LIST.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pt-3 mt-3 text-sm border-t border-n-weak">
        <p class="mb-1 text-n-slate-11">
          {{
            t('TELETEL_ROLES.LIST.WITHOUT_ROLE', {
              count: agentsWithoutRole.length,
            })
          }}
        </p>
        <ul v-if="agentsWithoutRole.length" class="flex flex-wrap gap-2">
          <li
            v-for="agent in agentsWithoutRole"
            :key="agent.id"
            class="flex items-center gap-2 px-2 py-1 text-xs border rounded-full border-n-weak"
          >
            <img
              v-if="agent.thumbnail"
              :src="agent.thumbnail"
              :alt="agent.name"
              class="rounded-full size-4"
            />
            <span class="text-n-slate-12">{{ agent.name }}</span>
            <span class="text-n-slate-10">- {{ baseRoleLabel(agent) }}</span>
          </li>
        </ul>
      </div>
    </ContentCard>

    <ContentCard
      v-if="editorOpen"
      ref="editorCard"
      :title="t('TELETEL_ROLES.FORM.TITLE')"
    >
      <div class="flex flex-col gap-4 text-sm">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label>{{ t('TELETEL_ROLES.FORM.NAME') }}</label>
            <input v-model="editing.name" class="mb-0" />
          </div>
          <div class="flex flex-col gap-1">
            <label>{{ t('TELETEL_ROLES.FORM.ROLE_DESCRIPTION') }}</label>
            <input v-model="editing.description" class="mb-0" />
          </div>
        </div>

        <!-- One card per permission group, side by side across the width;
             a single long column was unreadable on a wide screen. -->
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <section
            v-for="group in visibleGroups"
            :key="group.key"
            class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <p class="pb-2 font-medium border-b text-n-slate-12 border-n-weak">
              {{ t(`TELETEL_ROLES.GROUPS.${group.key}`) }}
            </p>
            <div
              v-for="key in group.keys"
              :key="key"
              class="flex flex-col gap-1"
            >
              <div class="flex items-center gap-2">
                <label class="flex items-center flex-1 gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    class="mb-0"
                    :checked="editing.permissions.includes(key)"
                    @change="togglePermission(key)"
                  />
                  {{ t(`TELETEL_ROLES.PERMISSIONS.${key}`) }}
                </label>
                <button
                  type="button"
                  class="flex items-center justify-center flex-shrink-0 text-xs font-bold border rounded-full size-5 border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
                  :class="openDetail === key ? 'bg-n-alpha-2 text-n-brand' : ''"
                  :aria-expanded="openDetail === key"
                  :title="t('TELETEL_ROLES.FORM.DETAILS_TOGGLE')"
                  :aria-label="t('TELETEL_ROLES.FORM.DETAILS_TOGGLE')"
                  @click="toggleDetail(key)"
                >
                  {{ DETAIL_MARK }}
                </button>
              </div>
              <p
                v-if="openDetail === key"
                class="px-2 py-1.5 text-xs leading-5 rounded-lg bg-n-alpha-1 text-n-slate-11"
              >
                {{ t(`TELETEL_ROLES.PERMISSION_DETAILS.${key}`) }}
              </p>
            </div>
          </section>
        </div>

        <p
          class="px-3 py-2 text-xs leading-5 border rounded-lg border-n-weak text-n-slate-11"
        >
          <span class="font-medium text-n-slate-12">
            {{ t('TELETEL_ROLES.FORM.RULES_TITLE') }}:
          </span>
          {{ t('TELETEL_ROLES.PERMISSION_DETAILS.RULES') }}
        </p>

        <div class="flex gap-2">
          <NextButton
            sm
            :is-loading="uiFlags.isSaving"
            :disabled="!editing.name.trim()"
            @click="saveRole"
          >
            {{ t('TELETEL_ROLES.FORM.SAVE') }}
          </NextButton>
          <NextButton sm slate @click="editorOpen = false">
            {{ t('TELETEL_ROLES.FORM.CANCEL') }}
          </NextButton>
        </div>
      </div>
    </ContentCard>

    <ContentCard :title="t('TELETEL_ROLES.ASSIGN.TITLE')">
      <p class="mb-2 text-xs text-n-slate-10">
        {{ t('TELETEL_ROLES.ASSIGN.HINT') }}
      </p>
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <select
          v-model="assignment.userId"
          class="mb-0 w-72"
          @change="onPickAgent"
        >
          <option value="" disabled>
            {{ t('TELETEL_ROLES.ASSIGN.PICK_AGENT') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }} -
            {{
              agent.custom_role_id
                ? roleName(agent.custom_role_id)
                : t('TELETEL_ROLES.ASSIGN.NO_ROLE')
            }}
          </option>
        </select>
        <select v-model="assignment.roleId" class="w-56 mb-0">
          <option value="">
            {{ t('TELETEL_ROLES.ASSIGN.NO_ROLE') }}
          </option>
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.name }}
          </option>
        </select>
        <NextButton sm :disabled="!assignment.userId" @click="assignRole">
          {{ t('TELETEL_ROLES.ASSIGN.APPLY') }}
        </NextButton>
      </div>
    </ContentCard>
  </div>
</template>
